// 261010 Epic 2 / P2b-3 + P8c — bước 3 ĐẠO DIỄN & DIỄN XUẤT: nhân vật cả video + diễn xuất từng shot. Form cỡ/góc/máy / nhân vật từng shot / thoại gắn shot đã dời sang bước 4 Quay phim.
import React from 'react';
import { Input, Select } from 'antd';
import type { EventPlan, FilmEvent } from '../../types/film';
import WorkflowEditFrame from './WorkflowEditShared';
import { needsSplitOf, splitNoteOf } from './workflowManyUtils';
import type { InteractiveProps } from './WorkflowStepShared';
import { applyEventCast, castOf, diffToOps } from './workflowEditOps';
import { shotLine } from './workflowDetailUtils';
import { shotAnchorId, shotPositions } from './stepReviewPolicy';
import WorkflowActingPanel from './WorkflowActingPanel'; // 261010 P3e
import { ShotCueDetail } from './ActingCueBlocks';
import { ContinuityBlock, StagingChip } from './WorkflowWarnBlocks'; // 261010 P9D D7

interface Props extends InteractiveProps {
  eventId: string;
  ev: FilmEvent;
  detail: EventPlan | null;
}

/** Chế độ xem — giữ nguyên như P2a. */
export const DirectingView: React.FC<{ ev: FilmEvent }> = ({ ev }) => {
  const shots = ev.shots || [];
  if (!shots.length) return <p className="muted" style={{ margin: '6px 0' }}>Chưa có shot nào — dùng «Dựng theo diễn xuất» ở trên hoặc chặng 2 của Máy tự chạy.</p>;
  const pos = shotPositions(shots); // neo theo VỊ TRÍ đã sắp (phiếu AI chấm: "Shot N" = vị trí N), không theo orderIndex
  return (
    <ul className="wf-list">{shots.map((s) => (
      <li key={s.id} id={shotAnchorId(pos.get(s.id) ?? s.orderIndex)}><span className="k">#{s.orderIndex}</span><b className="mono">{shotLine(s)}</b>
        {s.action ? <div>{s.action}</div> : null}
        {s.performance ? <div className="muted">Diễn xuất: {s.performance}</div> : null}
        {s.motion ? <div className="muted">Chuyển động: {s.motion}</div> : null}
        <ShotCueDetail shot={s} plan={ev.actingPlan} />
      </li>))}
    </ul>
  );
};

const WorkflowDirectingStep: React.FC<Props> = (props) => {
  const { detail, planId, eventId, ev, jobs, onActionDone, onJobsRefresh, actingFlags, actingOutcomes } = props;
  return (
    <>
    <div style={{ margin: '4px 0' }}><StagingChip detail={detail} /></div>
    {/* 261010 UI2 (L2/L3): video vượt trần giây của engine ⇒ nói rõ, máy tự chia khi chạy Đạo diễn (giữ trọn chữ). */}
    {needsSplitOf(ev) ? <p className="wf-alert WARN" role="status" data-testid="wf-needs-split">{splitNoteOf(needsSplitOf(ev)!)}</p> : null}
    {/* 261010 P3e — thẻ Diễn xuất: cue, cờ lỗi mềm, nhãn sửa tay + nút chạy lại, luật, ngoại lệ tập */}
    <WorkflowActingPanel planId={planId} eventId={eventId} ev={ev} jobs={jobs} flags={actingFlags ?? []} outcomes={actingOutcomes} onActionDone={onActionDone} onJobsRefresh={onJobsRefresh} />
    <ContinuityBlock ev={ev} jobs={jobs} onActionDone={onActionDone} />
    <WorkflowEditFrame
      {...props} stepKey="DIRECTING"
      view={(e) => <DirectingView ev={e} />}
      renderEditor={({ draft, update, base, bank, saving }) => {
        if (!draft.shots.length) return <p className="muted">Chưa có shot nào — dùng «Dựng theo diễn xuất» ở trên hoặc chặng 2 của Máy tự chạy.</p>;
        const who = castOf(draft.shots);
        const names = Array.from(new Set([...(bank?.characters || detail?.refBank?.characters || []).map((c) => c.name), ...castOf(base.shots)]));
        const pos = shotPositions(draft.shots);
        const changed = new Set(diffToOps(base, draft).flatMap((o) => (o.type === 'updateShot' ? [o.shotId] : [])));
        return (
          <div>
            <h3>Nhân vật xuất hiện trong video</h3>
            <Select mode="multiple" size="small" style={{ minWidth: 280 }} placeholder="Chọn nhân vật" value={who} disabled={saving} aria-label="Nhân vật xuất hiện trong video"
              options={names.map((n) => ({ label: n, value: n }))}
              onChange={(v: string[]) => update((d) => { applyEventCast(d.shots, v); })} />
            <p className="wf-note" style={{ margin: '4px 0 8px' }}>Bỏ một tên ⇒ gỡ khỏi mọi shot; thêm tên mới ⇒ gắn vào shot đầu (giống UI cũ). Sửa cỡ / góc / máy, nhân vật từng shot, thoại gắn shot, vị trí máy ở bước 4 Quay phim.</p>
            <h3>Shot</h3>
            {draft.shots.map((s) => (
              <div key={s.id} id={shotAnchorId(pos.get(s.id) ?? s.orderIndex)}>
                <div className="muted" style={{ fontSize: 13, marginBottom: 4 }}><span className="k">#{s.orderIndex}</span> {shotLine(s)}{changed.has(s.id) ? ' · đang sửa diễn xuất' : ''}</div>
                <div className="wf-cue-edit">
                  <label htmlFor={`perf-${s.id}`}>Diễn xuất của shot</label>
                  <Input.TextArea id={`perf-${s.id}`} rows={2} disabled={saving} value={s.performance ?? ''} data-testid={`wf-perf-${s.id}`}
                    onChange={(e) => { const v = e.target.value; update((d) => { const sh = d.shots.find((x) => x.id === s.id); if (sh) sh.performance = v === '' ? null : v; }); }} />
                  {(s.performance ?? null) !== (base.shots.find((x) => x.id === s.id)?.performance ?? null) && <span className="wf-cue-trace warn">Lưu ô này ⇒ video được đánh dấu sửa tay — AI không ghi đè (chưa có nút gỡ dấu).</span>}
                  <ShotCueDetail shot={s} plan={ev.actingPlan} />
                </div>
              </div>
            ))}
          </div>
        );
      }}
    />
    </>
  );
};

export default WorkflowDirectingStep;
